import { JSONPath } from 'jsonpath-plus';

import { EXPRESSION_TYPES, StepDefinitionId, Temporal } from '@willow/types-iso';

import { functionFrom, FunctionLibrary, getFunctionSignature } from '../FunctionDefinition';

const workflowLaunchDateTime = functionFrom(
  'workflowLaunchTime',
  [] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx) => ctx.workflowLaunchTime,
  'Returns the ZonedDateTime when the workflow was launched.',
);

const workflowLaunchPlainDate = functionFrom(
  'workflowLaunchDate',
  [] as const,
  EXPRESSION_TYPES.plainDate,
  (ctx) => ctx.workflowLaunchTime.toPlainDate(),
  'Date-only portion of the workflow launch time.',
);

const stepStatus = functionFrom(
  'stepStatus',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.string,
  (ctx, stepId) => {
    const step = ctx.getStepByDefinitionId(stepId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${stepId} not found`);
    }
    return step.status;
  },
  'Returns the current status string of the specified step.',
);

const stepStatusLastUpdated = functionFrom(
  'stepStatusLastUpdated',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx, stepId) => {
    const step = ctx.getStepByDefinitionId(stepId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${stepId} not found`);
    }
    return step.statusLastUpdated;
  },
  'Timestamp of the last status change for the specified step.',
);

const formIsSubmitted = functionFrom(
  'formIsSubmitted',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    return step.formSubmissionForLastActivation != null;
  },
  'Boolean indicating whether the specified form step has a submission.',
);

const plainDateFromForm = functionFrom(
  'plainDateFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.plainDate,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    return Temporal.PlainDate.from(value[0]);
  },
  'Extracts a PlainDate value from a submitted form via JSONPath.',
);

const stringFromForm = functionFrom(
  'stringFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.string,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    if (typeof value[0] !== 'string') {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value[0]}, expected a string`);
    }

    return value[0];
  },
  'Extracts a string value from a submitted form via JSONPath.',
);

const numberFromForm = functionFrom(
  'numberFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.number,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    if (typeof value[0] !== 'number' || Number.isNaN(value[0])) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value[0]}, expected a number`);
    }

    return value[0];
  },
  'Extracts a number value from a submitted form via JSONPath.',
);

const booleanFromForm = functionFrom(
  'booleanFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    if (typeof value[0] !== 'boolean') {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value[0]}, expected a boolean`);
    }

    return value[0];
  },
  'Extracts a boolean value from a submitted form via JSONPath.',
);

const formValueIsDefined = functionFrom(
  'formValueIsDefined',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    return value[0] != null;
  },
  'Checks whether a JSONPath in a form submission resolves to a non-null value.',
);

export const WORKFLOW_FUNCTIONS = {
  [getFunctionSignature(workflowLaunchDateTime)]: workflowLaunchDateTime,
  [getFunctionSignature(workflowLaunchPlainDate)]: workflowLaunchPlainDate,
  [getFunctionSignature(stepStatus)]: stepStatus,
  [getFunctionSignature(stepStatusLastUpdated)]: stepStatusLastUpdated,
  [getFunctionSignature(formIsSubmitted)]: formIsSubmitted,
  [getFunctionSignature(plainDateFromForm)]: plainDateFromForm,
  [getFunctionSignature(stringFromForm)]: stringFromForm,
  [getFunctionSignature(numberFromForm)]: numberFromForm,
  [getFunctionSignature(booleanFromForm)]: booleanFromForm,
  [getFunctionSignature(formValueIsDefined)]: formValueIsDefined,
} satisfies FunctionLibrary;
